import { useRef, useEffect, useState, useCallback } from "react"
import { Paperclip, ArrowUp, X, FileText, Image, FileSpreadsheet, Archive, Zap, Brain } from "lucide-react"

export interface AttachmentMeta {
  name: string
  type: string
  size: number
}

export interface ChatAttachmentReference {
  attachment_id: string
  original_name: string
  size: number
  expires_at?: string
  content_type?: string
  type?: string
  previewUrl?: string
}

type ComposerAttachment = {
  localId: string
  file: File
  status: "uploading" | "ready" | "error"
  error?: string
  ref?: ChatAttachmentReference
  previewUrl?: string
}

export type ModelSpeed = 'fast' | 'slow'

interface ChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSend: (message: string, attachments: ChatAttachmentReference[]) => void
  onUploadFile: (file: File) => Promise<ChatAttachmentReference>
  maxFileBytes?: number
  modelSpeed?: ModelSpeed
  onModelSpeedChange?: (speed: ModelSpeed) => void
}

const PLACEHOLDERS = [
  "Scrivi un messaggio...",
]

const MAX_FILES = 5

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function truncateName(name: string, max = 20): string {
  if (name.length <= max) return name
  const ext = name.lastIndexOf(".")
  if (ext > 0 && name.length - ext <= 6) {
    const base = name.slice(0, max - 3 - (name.length - ext))
    return `${base}...${name.slice(ext)}`
  }
  return `${name.slice(0, max - 3)}...`
}

function FileIcon({ type }: { type: string }) {
  if (type.startsWith("image/")) return <Image className="w-3 h-3 flex-shrink-0" />
  if (type.includes("spreadsheet") || type.includes("csv") || type.includes("excel"))
    return <FileSpreadsheet className="w-3 h-3 flex-shrink-0" />
  if (type.includes("zip") || type.includes("rar") || type.includes("tar") || type.includes("7z") || type.includes("archive"))
    return <Archive className="w-3 h-3 flex-shrink-0" />
  return <FileText className="w-3 h-3 flex-shrink-0" />
}

export function ChatComposer({ value, onChange, onSend, onUploadFile, maxFileBytes, modelSpeed = 'fast', onModelSpeedChange }: ChatComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [placeholderVisible, setPlaceholderVisible] = useState(true)
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const attachmentsRef = useRef<ComposerAttachment[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasUploading = attachments.some((item) => item.status === "uploading")
  const readyAttachments = attachments
    .filter((item) => item.status === "ready" && item.ref)
    .map((item) => item.ref as ChatAttachmentReference)
  const canSend = !hasUploading && (Boolean(value.trim()) || readyAttachments.length > 0)

  const showError = useCallback((msg: string) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current)
    setValidationError(msg)
    errorTimeoutRef.current = setTimeout(() => setValidationError(null), 3000)
  }, [])

  const validateAndAddFiles = useCallback((incoming: File[]) => {
    const currentCount = attachments.length
    const remaining = MAX_FILES - currentCount

    if (incoming.length > remaining) {
      showError(`Massimo ${MAX_FILES} file alla volta`)
      return
    }

    const valid: File[] = []
    for (const file of incoming) {
      if (file.type.startsWith("video/")) {
        showError("I file video non sono supportati")
        return
      }
      if (maxFileBytes !== undefined && file.size > maxFileBytes) {
        showError(`Il file "${truncateName(file.name)}" supera il limite di ${formatFileSize(maxFileBytes)}`)
        return
      }
      valid.push(file)
    }

    const items: ComposerAttachment[] = valid.map((file) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      status: "uploading",
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
    }))
    setAttachments((prev) => [...prev, ...items])

    for (const item of items) {
      onUploadFile(item.file)
        .then((ref) => {
          setAttachments((prev) =>
            prev.map((current) =>
              current.localId === item.localId
                ? {
                    ...current,
                    status: "ready",
                    ref: {
                      ...ref,
                      type: item.file.type,
                      previewUrl: item.previewUrl,
                    },
                  }
                : current
            )
          )
        })
        .catch(() => {
          setAttachments((prev) =>
            prev.map((current) =>
              current.localId === item.localId
                ? { ...current, status: "error", error: "errore upload" }
                : current
            )
          )
        })
    }
  }, [attachments.length, maxFileBytes, onUploadFile, showError])

  const removeFile = (localId: string) => {
    setAttachments((prev) => {
      const item = prev.find((candidate) => candidate.localId === localId)
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      return prev.filter((candidate) => candidate.localId !== localId)
    })
  }

  const handleSend = () => {
    if (!canSend) return
    onSend(value, readyAttachments)
    setAttachments([])
  }

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = "auto"
    const maxHeight = 5 * 20
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden"
  }, [value])

  // Rotating placeholder
  useEffect(() => {
    if (value) return
    const interval = setInterval(() => {
      setPlaceholderVisible(false)
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDERS.length)
        setPlaceholderVisible(true)
      }, 300)
    }, 3500)
    return () => clearInterval(interval)
  }, [value])

  useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  useEffect(() => {
    return () => {
      for (const item of attachmentsRef.current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      }
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cubone's file manager installs application-wide shortcuts on `window`
    // (including Ctrl/Cmd+V). Do not let keystrokes from the chat composer
    // reach those handlers, otherwise it tries to paste its null clipboard.
    e.stopPropagation()
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget?.files
    if (files) validateAndAddFiles(Array.from(files))
    if (e.currentTarget) e.currentTarget.value = ""
  }

  // Keep normal text paste behaviour, but treat files/images copied to the
  // clipboard exactly like files selected through the attachment button.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData?.files
    if (!files?.length) return

    e.preventDefault()
    validateAndAddFiles(Array.from(files))
  }, [validateAndAddFiles])

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length) validateAndAddFiles(Array.from(files))
  }, [validateAndAddFiles])

  const showOverlay = !value

  return (
    <div
      className="flex-shrink-0 relative"
      style={{ borderTop: "1px solid var(--hexa-border)", paddingTop: "12px" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg"
          style={{
            backgroundColor: "rgba(123,63,255,0.08)",
            border: "2px dashed var(--hexa-purple)",
          }}
        >
          <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--hexa-purple)" }}>
            Rilascia per allegare
          </span>
        </div>
      )}

      {/* Validation error */}
      {validationError && (
        <div
          className="mb-2 px-2"
          style={{ fontSize: "11px", color: "#ef4444", lineHeight: "1.3" }}
        >
          {validationError}
        </div>
      )}

      {/* Attached files preview */}
      {attachments.length > 0 && (
        <div
          className="mb-2 flex flex-wrap gap-1.5 overflow-x-auto scrollbar-hexa"
          style={{ maxHeight: "60px" }}
        >
          {attachments.map((item) => (
            <div
              key={item.localId}
              className="flex items-center gap-1.5 flex-shrink-0"
              style={{
                backgroundColor: "var(--hexa-surface-elevated)",
                border: item.status === "error" ? "1px solid #ef4444" : "1px solid var(--hexa-border)",
                borderRadius: "8px",
                padding: "4px 8px",
                fontSize: "11px",
                color: item.status === "error" ? "#ef4444" : "var(--hexa-text-secondary)",
              }}
            >
              <FileIcon type={item.file.type} />
              <span className="truncate" style={{ maxWidth: "120px" }}>
                {truncateName(item.file.name)}
              </span>
              <span style={{ color: "var(--hexa-text-muted)", whiteSpace: "nowrap" }}>
                {item.status === "uploading"
                  ? "..."
                  : item.status === "error"
                    ? "errore"
                    : formatFileSize(item.file.size)}
              </span>
              <button
                type="button"
                onClick={() => removeFile(item.localId)}
                className="flex-shrink-0 rounded-full transition-colors"
                style={{ color: "var(--hexa-text-muted)", padding: "1px" }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444" }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--hexa-text-muted)" }}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className="flex items-end gap-2 rounded-full border"
        style={{
          backgroundColor: "var(--hexa-surface-elevated)",
          borderColor: "rgba(106,77,255,0.25)",
          padding: "6px 8px 6px 12px",
        }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* Attachment button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Allega file"
          className="flex-shrink-0 p-1 rounded-full transition-colors self-center"
          style={{ color: "var(--hexa-text-muted)" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--hexa-text-primary)" }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--hexa-text-muted)" }}
        >
          <Paperclip className="w-4 h-4" />
        </button>

        {/* Model speed toggle */}
        {onModelSpeedChange && (
          <button
            type="button"
            onClick={() => onModelSpeedChange(modelSpeed === 'fast' ? 'slow' : 'fast')}
            className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full transition-all self-center"
            style={{
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              backgroundColor: modelSpeed === 'fast'
                ? "rgba(123,63,255,0.12)"
                : "rgba(245,158,11,0.12)",
              color: modelSpeed === 'fast'
                ? "var(--hexa-purple)"
                : "#f59e0b",
              border: `1px solid ${modelSpeed === 'fast' ? "rgba(123,63,255,0.25)" : "rgba(245,158,11,0.25)"}`,
            }}
            aria-label={modelSpeed === 'fast' ? "Modalità veloce: passa a ragionamento" : "Modalità ragionamento: passa a veloce"}
            title={modelSpeed === 'fast' ? "Modalit\u00e0 veloce — clicca per ragionamento" : "Modalit\u00e0 ragionamento — clicca per veloce"}
          >
            {modelSpeed === 'fast' ? (
              <Zap className="w-3 h-3" />
            ) : (
              <Brain className="w-3 h-3" />
            )}
            <span className="hidden sm:inline">{modelSpeed === 'fast' ? "Veloce" : "Ragiona"}</span>
          </button>
        )}

        {/* Textarea with animated placeholder overlay */}
        <div className="flex-1 min-w-0 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={(e) => e.stopPropagation()}
            onPaste={handlePaste}
            rows={1}
            aria-label="Messaggio"
            className="w-full resize-none bg-transparent text-base sm:text-sm focus:outline-none scrollbar-hexa"
            style={{
              color: "var(--hexa-text-primary)",
              lineHeight: "20px",
            }}
          />
          {showOverlay && (
            <span
              className="absolute inset-x-0 top-0 truncate pointer-events-none text-base sm:text-sm"
              style={{
                lineHeight: "20px",
                color: "var(--hexa-text-muted)",
                opacity: placeholderVisible ? 1 : 0,
                transition: "opacity 300ms ease",
              }}
            >
              {PLACEHOLDERS[placeholderIndex]}
            </span>
          )}
        </div>

        {/* Send */}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Invia messaggio"
          className="flex-shrink-0 flex items-center justify-center rounded-full transition-all self-center"
          style={{
            width: "30px",
            height: "30px",
            backgroundColor: canSend ? "var(--hexa-purple)" : "var(--hexa-surface-hover)",
            opacity: canSend ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (canSend) e.currentTarget.style.backgroundColor = "var(--hexa-purple-hover)"
          }}
          onMouseLeave={(e) => {
            if (canSend) e.currentTarget.style.backgroundColor = "var(--hexa-purple)"
          }}
        >
          <ArrowUp className="w-4 h-4" style={{ color: canSend ? "#fff" : "var(--hexa-text-muted)" }} />
        </button>
      </div>
    </div>
  )
}
